import React, { useState, useEffect, useRef } from 'react';
import { fetchInitialState, postCommand, setApiToken } from './api';
import './App.css';

// --- FIXED: WinModal is now defined OUTSIDE of App ---
const WinModal = ({ onPlayAgain }) => {
    const [copied, setCopied] = useState(false);
    const code = "CTF-WINNER-2024"; // Placeholder Text
    const redeemLink = "https://example.com/redeem"; // Placeholder Link

    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content win-modal">
                <div className="modal-header" style={{ justifyContent: 'center' }}>
                    <h3 className="glitch-text" style={{ color: 'var(--success-color)' }}>MISSION ACCOMPLISHED</h3>
                </div>
                <div className="modal-body" style={{ textAlign: 'center' }}>
                    <p className="output-line line-success">You did it - Silas is out of our network.</p>
                    <p className="output-line line-system">The Shadow Syndicate have been defeated... this time.</p>
                    
                    <div className="win-actions">
                        <div className="code-box">
                            <span>{code}</span>
                            <button onClick={handleCopy} className="action-btn">
                                {copied ? "Copied!" : "Copy Code"}
                            </button>
                        </div>
                        
                        <a href={redeemLink} target="_blank" rel="noopener noreferrer" className="action-btn redeem-btn">
                            Redeem Code
                        </a>

                        <button onClick={onPlayAgain} className="action-btn play-again-btn">
                            Play Again
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Main App Component ---
function App() {
    const [lines, setLines] = useState([]);
    const [prompt, setPrompt] = useState({ user: 'guest', host: '@aegis_console' });
    const [isFinished, setIsFinished] = useState(false);
    const [isWin, setIsWin] = useState(false);
    const [modal, setModal] = useState({ isOpen: false, title: '', body: [] });
    const [commandHistory, setCommandHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);

    const [timeLeft, setTimeLeft] = useState(null);
    const timerIdRef = useRef(null); 
    
    useEffect(() => {
        return () => {
            if (timerIdRef.current) {
                clearInterval(timerIdRef.current);
            }
        };
    }, []);

    useEffect(() => {
        async function load() {
            try {
                const { data } = await fetchInitialState();
                setLines(data.lines);
                setPrompt(data.prompt);
                if (data.token) {
                    setApiToken(data.token);
                }
            } catch (error) {
                console.error('Failed to initialize game:', error);
                setLines([{ message: 'Error connecting to server. Please refresh.', type: 'error' }]);
            }
        }
        load();
    }, []);

    const handleCommandSubmit = async (commandStr) => {
        // Allow 'y' or 'yes' through even if finished, to handle restart
        if (isFinished && commandStr.toLowerCase() !== 'y' && commandStr.toLowerCase() !== 'yes') {
             return; 
        }

        if (modal.isOpen) {
            setModal(prev => ({ ...prev, body: [...prev.body, { message: `> ${commandStr}`, type: 'command' }] }));
        } else {
            setLines(prev => [...prev, { message: `> ${commandStr}`, type: 'command' }]);
        }
        setCommandHistory(prev => [commandStr, ...prev]);
        setHistoryIndex(-1);

        try {
            const { data } = await postCommand(commandStr);
            
            if (data.inModal !== null) {
                setModal(prev => ({ ...prev, body: [...prev.body, ...data.lines] }));
            } else {
                setLines(prev => [...prev, ...data.lines]);
            }

            if (data.newPrompt) setPrompt(data.newPrompt);
            
            if (data.isFinished) {
                setIsFinished(true);
                if (timerIdRef.current) { 
                    clearInterval(timerIdRef.current);
                    timerIdRef.current = null;
                }
                setTimeLeft(null);
            }

            if (data.isWin) {
                setIsWin(true);
            }
            
            // Reset state on restart
            if (isFinished && !data.isFinished) {
                setIsFinished(false);
                setIsWin(false);
                setLines(data.lines); 
            }
            
            if (data.token) {
                setApiToken(data.token);
            }
            
            if (data.inModal && !modal.isOpen) {
                setModal({ isOpen: true, title: data.modalTitle, body: data.modalBody });
            } else if (data.inModal === null && modal.isOpen) {
                setModal({ isOpen: false, title: '', body: [] });
            }

            if (data.timerInfo) {
                setTimeLeft(data.timerInfo.timer); 
                if (!timerIdRef.current && !data.isFinished) { 
                    timerIdRef.current = setInterval(() => {
                        setTimeLeft(prev => {
                            if (prev === null || prev <= 1) {
                                clearInterval(timerIdRef.current);
                                timerIdRef.current = null;
                                handleCommandSubmit('status'); 
                                return null;
                            }
                            return prev - 1; 
                        });
                    }, 1000);
                }
            }

        } catch (error) {
            console.error('Command failed:', error);
            const errorLine = { message: 'Error communicating with server.', type: 'error' };
            if (modal.isOpen) {
                setModal(prev => ({ ...prev, body: [...prev.body, errorLine] }));
            } else {
                setLines(prev => [...prev, errorLine]);
            }
        }
    };

    const handleCloseModal = () => {
        handleCommandSubmit('exit');
    };

    const handlePlayAgain = () => {
        window.location.reload();
    };

    return (
        <div className="app-container">
            <div className="main-grid">
                <div className="terminal-container">
                    
                    {isWin && <WinModal onPlayAgain={handlePlayAgain} />}

                    {modal.isOpen && !isWin && (
                        <Modal 
                            title={modal.title} 
                            body={modal.body} 
                            onSubmit={handleCommandSubmit}
                            onClose={handleCloseModal}
                            prompt={prompt}
                            disabled={isFinished}
                        />
                    )}
                    <div className="terminal">
                        <OutputPanel lines={lines} />
                        <InputLine 
                            prompt={prompt} 
                            onSubmit={handleCommandSubmit}
                            disabled={isWin} 
                            history={commandHistory}
                            historyIndex={historyIndex}
                            setHistoryIndex={setHistoryIndex}
                        />
                    </div>
                </div>
                <NotesPanel />
            </div>
        </div>
    );
}

const OutputPanel = ({ lines }) => {
    const endOfPanelRef = useRef(null);
    useEffect(() => {
        endOfPanelRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [lines]);
    return (
        <div id="output-panel">
            {lines.map((line, index) => (
                <div key={index} className={`output-line line-${line.type}`}>
                    {line.message}
                </div>
            ))}
            <div ref={endOfPanelRef} />
        </div>
    );
};

const InputLine = ({ prompt, onSubmit, disabled, history, historyIndex, setHistoryIndex }) => {
    const [command, setCommand] = useState('');
    const inputRef = useRef(null);
    useEffect(() => {
        if (!disabled) {
            inputRef.current?.focus();
        }
    }, [disabled]);
    const handleSubmit = (e) => {
        e.preventDefault();
        if (disabled || command.trim() === '') return;
        onSubmit(command);
        setCommand('');
    };
    const handleKeyDown = (e) => {
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (history.length > 0) {
                const newIndex = Math.min(historyIndex + 1, history.length - 1);
                setHistoryIndex(newIndex);
                setCommand(history[newIndex] || '');
            }
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (historyIndex > -1) {
                const newIndex = Math.max(historyIndex - 1, -1);
                setHistoryIndex(newIndex);
                setCommand(history[newIndex] || '');
            }
        }
    };
    const isModal = prompt.user === '#';
    const isAdmin = prompt.user === 'admin';
    const isSiem = prompt.host.includes('siem');
    return (
        <form id="input-line" onSubmit={handleSubmit}>
            <span className={`prompt-user ${isModal ? 'modal' : ''} ${isAdmin ? 'admin' : ''}`}>{prompt.user}</span>
            <span className={`prompt-host ${isSiem ? 'siem' : ''}`}>{prompt.host}</span>
            {!isModal && <span className="text-green-400 mr-2">&gt;</span>}
            <input 
                type="text" 
                id="command-input" 
                ref={inputRef}
                value={command}
                onChange={(e) => setCommand(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                autoFocus
                autoComplete="off"
            />
        </form>
    );
};

const NotesPanel = () => (
    <div className="notes-paper">
        <h3 className="text-2xl">Investigation Notes</h3>
        <div className="space-y-4">
            <div>
                <p className="font-bold underline">System Components</p>
                <p>Main components identified: <strong>ad_controller, siem_console, vpn_gateway, sap_erp, jenkins_master</strong>.</p>
            </div>
            <div>
                <p className="font-bold underline">Analysis Options</p>
                <p className="text-xs">• Network traffic analysis</p>
                <p className="text-xs">• Artifact forensics</p>
                <p className="text-xs">• System exploration</p>
            </div>
            <div>
                <p>...must protect the main asset at all costs...</p>
            </div>
             <div>
                <p className="font-bold underline">Recovered Artifact</p>
                <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                     alt="Encrypted memo"
                     title="memo_fragment_recovered.png" />
                <p className="text-xs">File metadata: Author="S_Ops_Team", Keywords="defense,warning"</p>
            </div>
        </div>
    </div>
);

const Modal = ({ title, body, onSubmit, onClose, prompt, disabled }) => {
    const endOfPanelRef = useRef(null);
    useEffect(() => {
        endOfPanelRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [body]);
    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-header">
                    <h3 className="glitch-text">{title}</h3>
                    <button className="modal-close" onClick={onClose}>&times;</button>
                </div>
                <div className="modal-body">
                    {body.map((line, index) => (
                        <div key={index} className={`output-line line-${line.type}`}>
                            {line.message}
                        </div>
                    ))}
                    <div ref={endOfPanelRef} />
                </div>
                <InputLine 
                    prompt={prompt} 
                    onSubmit={onSubmit}
                    disabled={disabled}
                    history={[]} 
                    historyIndex={-1}
                    setHistoryIndex={() => {}}
                />
            </div>
        </div>
    );
};

export default App;